import { useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import OcrUpload from "../components/pages/generic-ocr/OcrUpload";
import OcrResultView from "../components/pages/generic-ocr/OcrResultView";
import { genericOcr, getGenericOcrResult } from "../service/apiActions/OcrAction";
import { OutletcontextType } from "../components/Layout/Index";
import Loader from "../components/Common/Loader/Loader";
import { FormControl, InputLabel, MenuItem, Select, Stack } from "@mui/material";

// Poll the result endpoint every 2s and give up after 2 minutes.
const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;

export default function OCRDashboard() {
    const [result, setResult] = useState<Record<string, any> | null>(null);
    const [file, setFile] = useState<File | null>(null);
    const [isLoading, setLoading] = useState(false);
    const { showSnackbar } = useOutletContext<OutletcontextType>();

    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const pollStartRef = useRef<number>(0);

    const stopPolling = () => {
        if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
    };

    useEffect(() => {
        document.title = "AiFise | Document OCR";
        stopPolling();

        return () => { stopPolling() }
    }, [])

    const startPolling = (id: string) => {
        stopPolling();
        pollStartRef.current = Date.now();

        pollRef.current = setInterval(async () => {
            if (Date.now() - pollStartRef.current > POLL_TIMEOUT_MS) {
                stopPolling();
                setLoading(false);
                showSnackbar("OCR is taking longer than expected. Please try again.");
                return;
            }

            try {
                const res: any = await getGenericOcrResult(id);
                const status = String(res?.status ?? "").toLowerCase();
                console.log("res===", res);

                if (status === "success") {
                    stopPolling();
                    setLoading(false);
                    setResult(res?.response ?? {});
                } else if (status === "failed" || status === "failure" || status === "error") {
                    stopPolling();
                    setLoading(false);
                    showSnackbar("OCR processing failed. Please try another document.");
                }
                // Any other status (e.g. processing/pending) -> keep polling.
            } catch (error) {
                // Transient error while processing -> keep polling until timeout.
            }
        }, POLL_INTERVAL_MS);
    };

    const onUpload = (files: File[], modelName: string) => {
        if (!files?.length) return;

        const formData = new FormData();
        formData.append("file", files[0]);
        formData.append("model", modelName)

        setLoading(true);
        genericOcr(formData)
            .then((res: any) => {
                const id = res?.id ?? res?.data?.id;
                setFile(files[0]);
                if (id) {
                    startPolling(id);
                } else {
                    setLoading(false);
                    showSnackbar("File upload failed. Please try again.");
                }
            })
            .catch(() => {
                setLoading(false);
                showSnackbar("Something went wrong while uploading the file.");
            });
    };

    if (isLoading) {
        return <Loader />;
    }

    if (result) {
        return (
            <OcrResultView
                previewImage={file ? URL.createObjectURL(file) : null}
                result={result}
                isPdf={/^.+\.pdf$/.test(file?.name as string)}
                reviewThreshold={95}
                onReset={() => {
                    setResult(null);
                    setFile(null);
                    setLoading(false);
                    stopPolling();
                }}
            />
        );
    }

    return <OcrUpload onSubmit={onUpload} multiple={false} />;
}
